import { useEffect, useMemo, useState } from "react";
import { siteTexts } from "../i18n/siteTexts";
import "./EventLocationActions.css";

type EventLocationActionsProps = {
  address?: string | null;
  city?: string | null;
  fallback: string;
  showLeadingIcon?: boolean;
  state?: string | null;
  valueAs?: "span" | "strong";
  variant?: "pill" | "fact";
};

function buildLocationLabel(
  address?: string | null,
  city?: string | null,
  state?: string | null,
  fallback?: string,
) {
  const street = String(address || "").trim();
  const locality = [city, state]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" / ");
  return [street, locality].filter(Boolean).join(" - ") || String(fallback || "");
}

function buildLocationQuery(
  address?: string | null,
  city?: string | null,
  state?: string | null,
) {
  return [address, city, state]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(", ");
}

export default function EventLocationActions({
  address,
  city,
  fallback,
  showLeadingIcon = false,
  state,
  valueAs = "span",
  variant = "pill",
}: EventLocationActionsProps) {
  const texts = siteTexts.common.locationActions;
  const [copyState, setCopyState] = useState<"idle" | "success" | "error">(
    "idle",
  );

  const label = useMemo(
    () => buildLocationLabel(address, city, state, fallback),
    [address, city, fallback, state],
  );
  const query = useMemo(
    () => buildLocationQuery(address, city, state),
    [address, city, state],
  );
  const mapUrl = query
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
    : "";
  const canInteract = Boolean(query);
  const ValueTag = valueAs;

  useEffect(() => {
    if (copyState === "idle") return undefined;
    const timeout = window.setTimeout(() => setCopyState("idle"), 1600);
    return () => window.clearTimeout(timeout);
  }, [copyState]);

  const handleCopy = async () => {
    if (!query) return;
    try {
      await navigator.clipboard.writeText(query);
      setCopyState("success");
    } catch (_error) {
      setCopyState("error");
    }
  };

  const copyTitle =
    copyState === "success"
      ? texts.copied
      : copyState === "error"
        ? texts.copyError
        : texts.copyAddress;
  const copyIcon =
    copyState === "success"
      ? "check"
      : copyState === "error"
        ? "error"
        : "content_copy";

  return (
    <div className={`location-actions location-actions--${variant}`}>
      {canInteract ? (
        <a
          className="location-actions__value location-actions__value--interactive"
          href={mapUrl}
          target="_blank"
          rel="noreferrer"
          title={texts.openMap}
        >
          {showLeadingIcon ? (
            <span className="material-symbols-outlined" aria-hidden="true">
              location_on
            </span>
          ) : null}
          <ValueTag>{label}</ValueTag>
        </a>
      ) : (
        <div className="location-actions__value">
          {showLeadingIcon ? (
            <span className="material-symbols-outlined" aria-hidden="true">
              location_on
            </span>
          ) : null}
          <ValueTag>{label}</ValueTag>
        </div>
      )}

      {canInteract ? (
        <div className="location-actions__buttons">
          <a
            className="location-actions__button"
            href={mapUrl}
            target="_blank"
            rel="noreferrer"
            title={texts.openMap}
            aria-label={texts.openMap}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              map
            </span>
          </a>
          <button
            type="button"
            className="location-actions__button"
            onClick={handleCopy}
            title={copyTitle}
            aria-label={copyTitle}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              {copyIcon}
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
